import React, { useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Stack,
  Divider,
  MenuItem,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Resolver } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";

import { FormProvider, RHFTextField, RHFSelect } from "@app/components";
import { useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";
import { Spacing } from "@app/components/spacing";
import { updateSalesPerson, customer } from "../slice";
import { selectUpdateSalesPerson } from "../selector";
import { addSalesPersonSchema } from "../validations";
import { IUserForm } from "../types";

interface EditSalespersonModalProps {
  open: boolean;
  onClose: () => void;
  selectedRowData: IUserForm | null;
}

const EditPersonPopup: React.FC<EditSalespersonModalProps> = ({
  open,
  onClose,
  selectedRowData,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const roleOptions = ["Agent", "Salesperson"];

  const methods = useForm<IUserForm>({
    resolver: yupResolver(
      addSalesPersonSchema,
    ) as unknown as Resolver<IUserForm>,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      profileImage: null,
    },
  });

  const profileImageFile = methods.watch("profileImage");

  // Prefill form when selectedRowData changes
  useEffect(() => {
    if (selectedRowData) {
      methods.reset({
        firstName: selectedRowData.firstName || "",
        lastName: selectedRowData.lastName || "",
        email: selectedRowData.email || "",
        phone: selectedRowData.phone || "",
        role: selectedRowData.role || "",
        // assignCustomer: selectedRowData.assignCustomer || "",
        profileImage: selectedRowData.profileImage || null,
      });
    }
  }, [selectedRowData, methods]);

  useEffect(() => {
    if (open) dispatch(customer("Customer"));
  }, [dispatch, open]);

  const onSuccess = useCallback(() => {
    onClose();
    methods.reset();
  }, [onClose, methods]);

  const onSubmit = (form: IUserForm) => {
    if (!selectedRowData?.id) return;
    try {
      dispatch(
        updateSalesPerson({
          id: selectedRowData.id,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          role: form.role,
          // assignCustomer: form.assignCustomer,
          profileImage: form.profileImage,
        }),
      );
    } catch (error) {
      console.error("Update User failed:", error);
    }
  };

  const { loading } = useRequestState({
    stateSelector: selectUpdateSalesPerson,
    errorShown: true,
    successMessageShown: true,
    onSuccess,
  });

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { minHeight: 368, minWidth: 620, borderRadius: 3 } }}
    >
      <FormProvider methods={methods}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <DialogTitle
              sx={{ p: 0, fontWeight: 500, fontFamily, color: "#000000" }}
            >
              {Strings.salesPerson.editSalesPerson}
            </DialogTitle>
            <IconButton onClick={onClose}>
              <CloseIcon htmlColor="#080808" />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={3} mt={1}>
            <RHFTextField name="firstName" label="First Name" size="small" />
            <RHFTextField name="lastName" label="Last Name" size="small" />
            <RHFTextField name="phone" label="Phone" size="small" />
            <RHFTextField name="email" label="Email" size="small" disabled />

            <RHFSelect name="role" label="Role" size="small">
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </RHFSelect>

            {/* <RHFSelect
              name="assignCustomer"
              label="Assign Customer"
              size="small"
            >
              {assignCustomer.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {`${option.firstName} ${option.lastName}`}
                </MenuItem>
              ))}
            </RHFSelect> */}
          </Stack>

          <Spacing top={8} />

          <Box
            sx={{
              height: 140,
              border: "2px solid #1E91B6",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: "100%", sm: "60%", md: "40%" },
              backgroundColor: "#F7F9FB",
              cursor: "pointer",
              overflow: "hidden",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {profileImageFile ? (
              <img
                src="d"
                alt="Preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Icon
                icon="fluent:camera-add-48-filled"
                width={40}
                color="#1E91B6"
              />
            )}
          </Box>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                methods.setValue("profileImage", file, {
                  shouldValidate: true,
                });
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontWeight: 700,
              px: 4,
              fontSize: "16px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              py: 1,
            }}
          >
            {Strings.button.close}
          </Button>

          <LoadingButton
            onClick={methods.handleSubmit(onSubmit)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              py: 1,
              color: "white",
            }}
            loading={loading}
          >
            {Strings.button.send}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export { EditPersonPopup };
